<template>
  <Modal v-if="showModal" class="second-factor-auth-modal" :style="modalStyles">
    <div class="flex column align-start gap20">
      <p v-if="!didSend">{{$t('authLocales.required2FactorAthMsg')}}</p>
      <p v-else-if="method === 'sms'">קוד אימות נשלח לטלפון שלך. אנא הזן את הקוד להמשך.</p>
      <p v-else-if="method === 'email'">קוד אימות נשלח לאימייל שלך. אנא הזן את הקוד להמשך.</p>
      <p v-else>קוד אימות נשלח. אנא הזן את הקוד להמשך.</p>
      <FormInput v-if="!didSend" class="method-input" label="authLocales.secondFactorMethodMsg" type="radio" v-model="method" :items="comunicationMethods"/>
      <button v-if="!didSend" @click="generatePass" :disabled="!method || resendCooldown > 0" class="btn big" :style="buttonStyle">
        {{resendCooldown > 0 ? `שלח שוב (${resendCooldown})` : 'שלח קוד'}}
      </button>
      <div v-if="didSend" class="flex column align-start gap20 width-all">
        <div class="flex align-center width-all space-between">
          <FormInput placeholder="authLocales.password" type="text" v-model="pass"/>
          <button @click="finishAuth" :disabled="!method" class="btn big" :style="buttonStyle">{{$t('login')}}</button>
        </div>
        <div class="flex align-center gap10 width-all">
          <button @click="changeMethod" class="btn" :style="{...buttonStyle, backgroundColor: '#f0f0f0', color: '#333'}">שנה שיטה</button>
          <button @click="generatePass" :disabled="resendCooldown > 0" class="btn big" :style="buttonStyle">
            {{resendCooldown > 0 ? `שלח שוב (${resendCooldown})` : 'שלח קוד מחדש'}}
          </button>
        </div>
      </div>
      <MiniLoader v-if="isLoading"/>
      <p v-else-if="didSend">{{$t('authLocales.secFactorPassExpirationMsg')}}</p>
    </div>
  </Modal>
</template>

<script>
import FormInput from '../../common/cmps/FormInput.vue'
import Modal from '../../common/cmps/Modal.vue'
import evEmmiter from '@/apps/common/modules/common/services/event-emmiter.service';
import { alertService } from '@/apps/common/modules/common/services/alert.service';
import MiniLoader from '../../common/cmps/MiniLoader.vue';
export default {
  name: 'SecondFactorAuthModal',
  components: { Modal, FormInput, MiniLoader },
  data() {
    return {
      pass: '',
      method: '',
      showModal: false,
      didSend: false,
      redirectEndpoint: '',
      requiredComunicationMethods: [],
      resendCooldown: 0,
      resendTimer: null
    }
  },

  computed: {
    loggedUser() {
      return this.$store.getters['auth/loggedUser'];
    },
    isLoading() {
      return this.$store.getters['auth/isLoading'];
    },
    selectedTheme() {
      return this.$store.getters.selectedTheme;
    },
    rootOrg() {
      return this.$store.getters.rootOrg;
    },
    theme() {
      // Get theme from selectedTheme or rootOrg
      return this.selectedTheme || (this.rootOrg?.designPreferences?.clientApp?.[0] || this.rootOrg?.designPreferences?.producerApp?.[0])
    },
    modalStyles() {
      if (!this.theme?.colors) return {}
      return {
        '--clr-0': this.theme.colors[0] || '#000',
        '--clr-1': this.theme.colors[1] || '#fff',
        '--clr-4': this.theme.colors[4] || this.theme.colors[0] || '#000',
        '--clr-6': this.theme.colors[6] || this.theme.colors[0] || '#000'
      }
    },
    buttonStyle() {
      // ALWAYS return a visible, non-gray color for buttons
      // Since theme might not be available or might have gray colors, always use safe defaults
      // The CSS might be overriding, so we need to be very explicit
      
      // For now, ALWAYS return blue to ensure visibility
      // We can enhance this later to use theme colors when they're available and not gray
      return {
        backgroundColor: '#2090d4',
        color: '#ffffff',
        border: 'none',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
      }
    },
    comunicationMethods() {
      const methods = [];
      if (this.requiredComunicationMethods?.length) {
        // Format methods from backend as {value, label} objects
        return this.requiredComunicationMethods.map(method => {
          if (typeof method === 'string') {
            return {
              value: method,
              label: method === 'sms' ? 'מסרון' : method === 'email' ? 'אימייל' : method
            };
          }
          return method;
        });
      }
      if (this.loggedUser?.email) methods.push({value: 'email', label: 'אימייל'});
      if (this.loggedUser?.mobile) {
        methods.push({value: 'sms', label: 'מסרון'});
        // methods.push({value: 'whatsapp', label: 'WhatsApp'}); // Commented out for future use
      }
      return methods;
    }
  },

  methods: {
    async generatePass() {
      if (!this.method) {
        return;
      }
      if (this.resendCooldown > 0) {
        return;
      }
      this.didSend = false;
      try {
        await this.$store.dispatch({ type: 'auth/makeSecondFactorAuthPass', method: this.method });
        this.didSend = true;
        this.startResendCooldown();
      } catch (err) {
        // Error is already handled by the store's onError callback
        // Don't set didSend = true if sending failed
        console.error('Failed to send authentication code:', err);
      }
    },
    startResendCooldown() {
      this.resendCooldown = 60;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      this.resendTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }, 1000);
    },
    changeMethod() {
      this.didSend = false;
      this.pass = '';
      this.resendCooldown = 0;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
    },
    async finishAuth() {
      await this.$store.dispatch({ type: 'auth/finish2FactorAuth', pass: this.pass, method: this.method });
      if (this.$route.query.endpoint) this.$router.push(this.$route.query.endpoint);
      else if (this.redirectEndpoint) this.$router.push(this.redirectEndpoint);
      else window.location.reload();
      this.showModal = false;
      this.redirectEndpoint = '';
      this.requiredComunicationMethods = [];
    },

    
    initComunicationMethod() {
      if (this.comunicationMethods.length > 0) {
        const firstMethod = this.comunicationMethods[0];
        this.method = typeof firstMethod === 'string' ? firstMethod : firstMethod.value;
      } else {
        this.method = '';
      }
    }
  },

  created() {
    this.initComunicationMethod();
    const on2AuthCb = async (endpoint, requiredComunicationMethods) => {
      if (this.showModal) return;
      this.showModal = true;
      if (endpoint) this.redirectEndpoint = endpoint;
      // else this.redirectEndpoint = this.$route.query.endpoint;
      if (requiredComunicationMethods) this.requiredComunicationMethods = requiredComunicationMethods;
      this.pass = '';
      this.didSend = false;
      this.resendCooldown = 0;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      // evEmmiter.off('needs_2_factor_auth', on2AuthCb);
      // try {
      //   if (this.loggedUser) await this.$store.dispatch({ type: 'auth/makeSecondFactorAuthPass' });
      //   const pass = await alertService.Prompt(this.$t('authLocales.required2FactorAthMsg'), this.$t('authLocales.password'));
      //   await this.$store.dispatch({ type: 'auth/finishAuth', pass });
      //   if (endpoint) this.$router.push(endpoint);
      //   evEmmiter.on('needs_2_factor_auth', on2AuthCb);
      // } catch(err) {
      //   if (err.needs2FactorAuth) on2AuthCb();
      //   // else evEmmiter.on('needs_2_factor_auth', on2AuthCb);
      // }
      // finally {
      //   evEmmiter.on('needs_2_factor_auth', on2AuthCb);
      // }
    }
    evEmmiter.on('needs_2_factor_auth', on2AuthCb);
  },
  beforeDestroy() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  },
  watch: {
    comunicationMethods() {
      this.initComunicationMethod();
    },
    theme: {
      handler() {
        // Force reactivity when theme changes
        this.$forceUpdate();
      },
      deep: true
    },
    selectedTheme: {
      handler() {
        // Force reactivity when selectedTheme changes
        this.$forceUpdate();
      },
      deep: true
    }
  }

}
</script>

<style lang="scss">
@import '@/assets/styles/global/index';
.second-factor-auth-modal {
  // The inline styles from modalStyles will set CSS variables on .modal-container
  .modal {
    width: em(400px);
    max-height: none !important;
    min-height: fit-content;
    overflow: visible !important;
    > *:not(.close-btn) {
      overflow: visible !important;
    }
  }
  .method-input {
    flex-direction: column;
    align-items: flex-start;
    gap: em(15px);
  }
  // Ensure text is visible with theme colors
  p {
    color: var(--clr-0, #000) !important;
  }
  // CRITICAL: Override global theme CSS that sets buttons to gray
  // The global CSS uses .btn:not(.clear) with var(--clr-0-l-20) which is gray
  // We need to override this with maximum specificity
  button.btn.big,
  .btn.big {
    // Force blue background - inline styles should override this, but this ensures visibility
    background-color: #2090d4 !important;
    color: #ffffff !important;
    border: none !important;
  }
}
</style>